'use client'

import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { statusLabel } from '@/lib/ap/invoices'
import type { ActionAttachmentData, ChatAttachment } from '@/lib/ai/types'

interface Row {
  label: string
  done: boolean
  from?: string | null
  to?: string | null
  reason?: string | null
}

function isDone(status?: string | null, reason?: string | null): boolean {
  if (status) return status === 'done' || status === 'success' || status === 'ok'
  return !reason
}

function rowsFrom(data: ActionAttachmentData): Row[] {
  if (Array.isArray(data.results) && data.results.length) {
    return data.results.map((r) => ({
      label: r.invoice_id_str ?? (r.invoice_id != null ? `#${r.invoice_id}` : 'Invoice'),
      done: isDone(r.status, r.reason),
      from: r.from_status,
      to: r.to_status,
      reason: r.reason,
    }))
  }
  return [
    {
      label: data.invoice_id_str ?? (data.invoice_id != null ? `#${data.invoice_id}` : ''),
      done: isDone(data.status, data.reason),
      from: data.from_status,
      to: data.to_status,
      reason: data.reason,
    },
  ]
}

/** "action" attachment — success / blocked pill with from → to and a reason. */
export function ActionCard({ attachment, onRefresh }: { attachment: ChatAttachment; onRefresh?: (() => void) | null }) {
  const data = (attachment.data ?? {}) as ActionAttachmentData
  const rows = rowsFrom(data)
  const doneCount = rows.filter((r) => r.done).length
  const allDone = doneCount === rows.length
  const multi = rows.length > 1

  return (
    <div className={cn('rounded-xl border p-3 shadow-soft', allDone ? 'border-emerald-200 bg-emerald-50/60' : doneCount === 0 ? 'border-red-200 bg-red-50/60' : 'border-amber-200 bg-amber-50/60')}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {allDone ? (
            <Icons.checkCircle className="h-4 w-4 text-emerald-600" strokeWidth={1.5} />
          ) : (
            <Icons.alertCircle className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
          )}
          <p className="text-sm font-medium text-brand-navy">{attachment.title}</p>
        </div>
        {multi && (
          <span className="text-[11px] text-muted-foreground">{doneCount}/{rows.length} done</span>
        )}
      </div>
      {attachment.description && <p className="mt-1 text-xs text-muted-foreground">{attachment.description}</p>}

      <ul className="mt-2 space-y-1.5">
        {rows.map((r, i) => (
          <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium', r.done ? 'bg-emerald-500/10 text-emerald-700' : 'bg-red-500/10 text-red-700')}>
              {r.done ? 'Done' : 'Blocked'}
            </span>
            {r.label && <span className="font-medium text-foreground">{r.label}</span>}
            {(r.from || r.to) && (
              <span className="text-muted-foreground">
                {r.from ? statusLabel(r.from) : '—'} <span aria-hidden>→</span> {r.to ? statusLabel(r.to) : '—'}
              </span>
            )}
            {r.reason && <span className="basis-full text-muted-foreground">{r.reason}</span>}
          </li>
        ))}
      </ul>

      {onRefresh && (
        <button type="button" onClick={onRefresh} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-brand-purple hover:underline">
          <Icons.refresh className="h-3 w-3" strokeWidth={1.5} /> Refresh page data
        </button>
      )}
    </div>
  )
}
